import dayjs from 'dayjs';
import { calculateDateRange } from './dates';

// Frames accepted by dashboard period controls (matches `calculateDateRange`
// in `utils/dates.js`). Used to validate URL values.
export const VALID_TIMEFRAMES = Object.freeze([
    'mtd', '3m', '6m', 'ytd', 'custom',
]);

/**
 * Preset frames forwarded to /api/analytics as a `period` token. `ytd` is NOT
 * a backend token — it falls through to explicit custom start/end below
 * (sending `period=ytd` gets a 400 and empty charts).
 */
export const PRESET_ANALYTICS_PERIODS = Object.freeze(
    VALID_TIMEFRAMES.filter((frame) => frame !== 'custom' && frame !== 'ytd'),
);

/** UI timeFrame → analytics API `period` token (mtd is sent as 30d). */
const ANALYTICS_PERIOD_BY_TIMEFRAME = Object.freeze({
    mtd: '30d',
});

function resolveAnalyticsPeriod(timeFrame) {
    return ANALYTICS_PERIOD_BY_TIMEFRAME[timeFrame] ?? timeFrame;
}

export const DEFAULT_TIMEFRAME = 'mtd';

// Format used for serialising `dayjs` ranges into the URL.
export const DATE_RANGE_PARAM_FORMAT = 'YYYY-MM-DD';

/**
 * Parses raw URL search-param values into orchestrator period state.
 *
 * @param {string | null | undefined} periodParam
 * @param {string | null | undefined} rangeParam
 * @param {string} [defaultTimeFrame=DEFAULT_TIMEFRAME]
 * @returns {{ timeFrame: string, dateRange: import('dayjs').Dayjs[] | null }}
 */
export function parsePeriodParams(periodParam, rangeParam, defaultTimeFrame = DEFAULT_TIMEFRAME) {
    const fallbackTimeFrame = VALID_TIMEFRAMES.includes(defaultTimeFrame) ? defaultTimeFrame : DEFAULT_TIMEFRAME;
    const timeFrame = VALID_TIMEFRAMES.includes(periodParam) ? periodParam : fallbackTimeFrame;

    if (timeFrame !== 'custom') return { timeFrame, dateRange: null };

    if (!rangeParam) return { timeFrame: fallbackTimeFrame, dateRange: null };

    const [rawStart, rawEnd] = rangeParam.split(',');
    const start = dayjs(rawStart, DATE_RANGE_PARAM_FORMAT);
    const end = dayjs(rawEnd, DATE_RANGE_PARAM_FORMAT);
    if (!start.isValid() || !end.isValid()) {
        return { timeFrame: fallbackTimeFrame, dateRange: null };
    }
    return { timeFrame: 'custom', dateRange: [start, end] };
}

/**
 * Serialises orchestrator period state into URL search-param values.
 *
 * @param {string} timeFrame
 * @param {import('dayjs').Dayjs[] | null | undefined} dateRange
 * @returns {{ period: string | null, range: string[] | null }}
 */
export function serializePeriodParams(timeFrame, dateRange) {
    const period = timeFrame || null;

    if (timeFrame !== 'custom' || !dateRange?.[0] || !dateRange?.[1]) {
        return { period, range: null };
    }

    return {
        period,
        range: [
            dateRange[0].format(DATE_RANGE_PARAM_FORMAT),
            dateRange[1].format(DATE_RANGE_PARAM_FORMAT),
        ],
    };
}

/**
 * Builds query params for `/api/analytics/*` period filters.
 *
 * Preset frames send the analytics token so the backend applies the correct
 * window (`mtd` in the UI maps to `period=30d`). `custom` (or `forceCustom`)
 * sends explicit `start`/`end` — used for user-selected ranges and prior-period
 * comparison windows.
 *
 * @param {string} timeFrame
 * @param {import('dayjs').Dayjs[] | null | undefined} [dateRange]
 * @param {{ start?: string, end?: string, forceCustom?: boolean }} [opts]
 * @returns {{ period: string, start?: string, end?: string }}
 */
export function buildAnalyticsPeriodParams(timeFrame, dateRange, opts = {}) {
    const { start: explicitStart, end: explicitEnd, forceCustom = false } = opts;

    if (forceCustom && explicitStart && explicitEnd) {
        return { period: 'custom', start: explicitStart, end: explicitEnd };
    }

    if (timeFrame === 'custom') {
        const { start, end } = calculateDateRange(timeFrame, dateRange);
        if (!start || !end) {
            return { period: 'custom' };
        }
        return { period: 'custom', start, end };
    }

    if (PRESET_ANALYTICS_PERIODS.includes(timeFrame)) {
        return { period: resolveAnalyticsPeriod(timeFrame) };
    }

    const { start, end } = calculateDateRange(timeFrame, dateRange);
    if (start && end) {
        return { period: 'custom', start, end };
    }

    return { period: timeFrame || '30d' };
}
